import { Button, Divider, Drawer, Empty, Pagination, Timeline, Typography } from "antd";
import { FiArrowRight, FiMail, FiClock } from "react-icons/fi";
import dayjs from "dayjs";
import { ApiService } from "api/api.service";
import { useParams } from "react-router-dom";
import { TbRefresh } from "react-icons/tb";
import { useState } from "react";

export type Card = {
  _id: string;
  title: string;
  reference_id: string;
  column_id: string;
  type: string;
  tags: string[];
};
// Types
type CardMovedLog = {
  id: string;
  type: "card_moved";
  cardId: string;
  card: Card;
  movedBy: { name: string; email: string };
  fromColumn: { id: string; name: string };
  toColumn: { id: string; name: string };
  timestamp: string;
};

type EmailSentLog = {
  id: string;
  type: "email_sent";
  cardId: string;
  card: Card;
  applicationId: string;
  templateName: string;
  to: string;
  status: "sent" | "failed";
  failReason: string | null;
  triggeredBy: string;
  timestamp: string;
  fromColumn?: {
    id: string;
    name: string;
  };
  toColumn?: {
    id: string;
    name: string;
  };
};

export type ActivityLog = CardMovedLog | EmailSentLog;

// Helpers
const getLogIcon = (log: ActivityLog) => {
  switch (log.type) {
    case "card_moved":
      return <FiArrowRight size={14} className="text-blue-600" />;
    case "email_sent":
      return <FiMail size={14} className={log.status === "failed" ? "text-red-600" : "text-purple-600"} />;
    default:
      return <FiClock size={14} className="text-gray-500" />;
  }
};

const getLogDescription = (log: ActivityLog): string => {
  switch (log.type) {
    case "card_moved":
      return `${log.movedBy.name} moved "${log?.card?.title}" from "${log?.fromColumn?.name || ""}" to "${log?.toColumn?.name || ""}"`;
    case "email_sent": {
      const actor = log.triggeredBy === "auto" ? "Automatically" : log.triggeredBy;
      const base = `${actor} sent "${log.templateName}" to ${log.to} at stage "${log.toColumn?.name}"`;
      return log.status === "failed" && log.failReason ? `${base} — failed: ${log.failReason}` : base;
    }
  }
};

const getLogColor = (log: ActivityLog): string => {
  switch (log.type) {
    case "card_moved":
      return "#3b82f6";
    case "email_sent":
      return log.status === "failed" ? "#ef4444" : "#a855f7";
  }
};

// Component
const PipelineActivity = ({ isDrawerOpen, onClose }: { isDrawerOpen: boolean; onClose: () => void }) => {
  const { id: boardId } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const activityLogsRes = ApiService.kanban.useGetBoardActivity({ boardId, page, limit: pageSize });
  const activityLogs = activityLogsRes.data?.result.data ?? [];
  const timelineItems = activityLogs
    .slice()
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .map((log) => {
      const color = getLogColor(log);

      return {
        dot: (
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm"
            style={{
              backgroundColor: `${color}15`,
              border: `1px solid ${color}40`,
            }}
          >
            {getLogIcon(log)}
          </div>
        ),
        children: (
          <div className="-mt-0.5 ml-1">
            <p className="text-foreground text-sm leading-snug font-medium">{getLogDescription(log)}</p>
            <p className="text-muted-foreground mt-0.5 text-xs">{dayjs(log.timestamp).format("MMM D, YYYY h:mm A")}</p>
          </div>
        ),
      };
    });

  return (
    <Drawer
      title={
        <div className="w-full flex items-center justify-between">
          <Typography.Paragraph style={{ margin: 0 }}>Pipeline Activity</Typography.Paragraph>
          <Button onClick={() => activityLogsRes.refetch()} icon={<TbRefresh />} size="small">
            Refresh
          </Button>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={isDrawerOpen}
      width={500}
      styles={{ body: { padding: "24px" } }}
    >
      <div className="space-y-6 px-6">
        <div className="flex items-center justify-between">
          <div>
            <Typography.Title level={5} className="text-lg font-semibold !mb-0 mt-4">
              Activity Log
            </Typography.Title>
            <p className="text-sm text-gray-500">
              {activityLogsRes.data?.result.totalObjects ?? 0} activities recorded
            </p>
          </div>
        </div>

        {activityLogs.length === 0 ? (
          <div>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity found" />
          </div>
        ) : (
          <div className="pr-2">
            <Timeline mode="left" items={timelineItems} className="!mt-2" style={{ paddingLeft: "8px" }} />

            <div className="flex justify-end mt-2 pb-4">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={activityLogsRes.data?.result.totalObjects}
                onChange={(newPage, newPageSize) => {
                  setPage(newPage);
                  setPageSize(newPageSize);
                }}
                showSizeChanger
                pageSizeOptions={[10, 20, 50]}
                size="small"
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default PipelineActivity;
